import { TextField } from '@mui/material';
import { Box, styled } from '@mui/system';
import * as React from 'react';

export const SystemOTP = ({
    length,
    value,
    onChange,
}: {
    length: number;
    value: string;
    onChange: (value: string) => void;
}) => {
    const inputRefs = React.useRef<HTMLInputElement[]>(new Array(length).fill(null));

    const focusInput = (targetIndex: number) => {
        const targetInput = inputRefs.current[targetIndex];
        targetInput.focus();
    };

    const selectInput = (targetIndex: number) => {
        const targetInput = inputRefs.current[targetIndex];
        targetInput.select();
    };

    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
        currentIndex: number,
    ) => {
        let prevOtp = value;

        switch (event.key) {
            case 'ArrowUp':
            case 'ArrowDown':
            case ' ':
                event.preventDefault();
                break;
            case 'ArrowLeft':
                event.preventDefault();
                if (currentIndex > 0) {
                    focusInput(currentIndex - 1);
                    selectInput(currentIndex - 1);
                }
                break;
            case 'ArrowRight':
                event.preventDefault();
                if (currentIndex < length - 1) {
                    focusInput(currentIndex + 1);
                    selectInput(currentIndex + 1);
                }
                break;
            case 'Delete':
                event.preventDefault();
                prevOtp = prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
                onChange(prevOtp);

                break;
            case 'Backspace':
                event.preventDefault();
                if (currentIndex > 0) {
                    focusInput(currentIndex - 1);
                    selectInput(currentIndex - 1);
                }

                prevOtp = prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
                onChange(prevOtp);
                break;

            default:
                break;
        }
    };

    const handleChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        currentIndex: number,
    ) => {
        const currentValue = event.target.value;
        let indexToEnter = 0;

        while (indexToEnter <= currentIndex) {
            if (inputRefs.current[indexToEnter].value && indexToEnter < currentIndex) {
                indexToEnter += 1;
            } else {
                break;
            }
        }

        // Build a fixed-length OTP array so we can always write the new value
        const otpArray = Array.from({ length }, (_, i) => value[i] ?? '');
        // Only keep the last typed character for this cell
        otpArray[indexToEnter] = currentValue.slice(-1);
        onChange(otpArray.join(''));

        if (currentValue !== '') {
            if (currentIndex < length - 1) {
                focusInput(currentIndex + 1);
            }
        }
    };

    const handleClick = (
        _event: React.MouseEvent<HTMLInputElement, MouseEvent>,
        currentIndex: number,
    ) => {
        selectInput(currentIndex);
    };

    const handlePaste = (
        event: React.ClipboardEvent<HTMLInputElement>,
        currentIndex: number,
    ) => {
        event.preventDefault();
        const clipboardData = event.clipboardData;

        // Check if there is text data in the clipboard
        if (clipboardData.types.includes('text/plain')) {
            let pastedText = clipboardData.getData('text/plain');
            pastedText = pastedText.substring(0, length).trim();
            let indexToEnter = 0;

            while (indexToEnter <= currentIndex) {
                if (inputRefs.current[indexToEnter].value && indexToEnter < currentIndex) {
                    indexToEnter += 1;
                } else {
                    break;
                }
            }

            const otpArray = value.split('');

            for (let i = indexToEnter; i < length; i += 1) {
                const lastValue = pastedText[i - indexToEnter] ?? ' ';
                otpArray[i] = lastValue;
            }

            onChange(otpArray.join(''));
        }
    };

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {new Array(length).fill(null).map((_, index) => (
                <React.Fragment key={index}>
                    <TextField
                        slots={{
                            input: InputElement,
                        }}
                        aria-label={`Digit ${index + 1} of OTP`}
                        autoFocus={index === 0}
                        slotProps={{
                            input: {
                                ref: (ele: HTMLInputElement) => {
                                    inputRefs.current[index] = ele!;
                                },
                                onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(event, index),
                                onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event, index),
                                onClick: (event: React.MouseEvent<HTMLInputElement>) => handleClick(event, index),
                                onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => handlePaste(event, index),
                                value: value[index] ?? '',
                            },
                        }}
                    />
                </React.Fragment>
            ))}
        </Box>
    );
}

const InputElement = styled('input')(
    () => `
  width: 40px;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  padding: 8px 0;
  border-radius: 8px;
  text-align: center;
  color: #1C2025;
  background: ${'#fff'};
  border: 1px solid #DAE2ED;
  box-shadow: 0 2px 4px ${'rgba(0,0,0, 0.05)'
        };

  &:hover {
    border-color: #3399FF;
  }

  &:focus {
    border-color: #3399FF;
    box-shadow: 0 0 0 3px #80BFFF;
  }

  /* firefox */
  &:focus-visible {
    outline: 0;
  }
`,
);
